import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listInstallers } from '../installers/api';
import { addNote, getTask, updateTask } from './api';
const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'complete', label: 'Complete' },
];
function statusBadgeStyle(status) {
    switch (status) {
        case 'complete':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(52,199,89,0.12)',
                color: '#248a3d',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        case 'in_progress':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(0,122,255,0.12)',
                color: '#0071e3',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        case 'blocked':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(255,59,48,0.12)',
                color: '#c0392b',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        case 'pending':
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(255,149,0,0.12)',
                color: '#b45309',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
        default:
            return {
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: 'rgba(142,142,147,0.15)',
                color: '#86868b',
                borderRadius: '980px',
                fontSize: '13px',
                fontWeight: '600',
            };
    }
}
function statusLabel(status) {
    return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}
export const TaskDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [installers, setInstallers] = useState([]);
    // Status change
    const [statusChanging, setStatusChanging] = useState(false);
    const [statusError, setStatusError] = useState(null);
    // Details editing
    const [editName, setEditName] = useState('');
    const [editInstructions, setEditInstructions] = useState('');
    const [editDueDate, setEditDueDate] = useState('');
    const [editAssignedInstallerId, setEditAssignedInstallerId] = useState('');
    const [detailsDirty, setDetailsDirty] = useState(false);
    const [detailsSaving, setDetailsSaving] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [detailsSaved, setDetailsSaved] = useState(false);
    // Notes
    const [noteText, setNoteText] = useState('');
    const [noteSubmitting, setNoteSubmitting] = useState(false);
    const [noteError, setNoteError] = useState(null);
    const loadTask = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const data = await getTask(id);
            setTask(data);
            setEditName(data.name);
            setEditInstructions(data.instructions ?? '');
            setEditDueDate(data.dueDate ?? '');
            setEditAssignedInstallerId(data.assignedInstallerId ?? '');
            setDetailsDirty(false);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load task');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => {
        loadTask();
    }, [loadTask]);
    useEffect(() => {
        ;
        (async () => {
            try {
                const result = await listInstallers({ status: 'active', limit: 100 });
                setInstallers(result.items);
            }
            catch {
                // non-fatal
            }
        })();
    }, []);
    const markDirty = () => {
        setDetailsDirty(true);
        setDetailsSaved(false);
    };
    const handleStatusChange = async (newStatus) => {
        if (!task || newStatus === task.status)
            return;
        setStatusChanging(true);
        setStatusError(null);
        try {
            const updated = await updateTask(task.id, { status: newStatus });
            setTask(updated);
        }
        catch (e) {
            setStatusError(e.message ?? 'Failed to update status');
        }
        finally {
            setStatusChanging(false);
        }
    };
    const handleSaveDetails = async () => {
        if (!task)
            return;
        setDetailsSaving(true);
        setDetailsError(null);
        try {
            const assignedInstaller = installers.find((i) => i.id === editAssignedInstallerId);
            const updated = await updateTask(task.id, {
                name: editName.trim() || task.name,
                instructions: editInstructions.trim() || undefined,
                dueDate: editDueDate || undefined,
                assignedInstallerId: editAssignedInstallerId || undefined,
                assignedInstallerName: editAssignedInstallerId
                    ? assignedInstaller
                        ? `${assignedInstaller.firstName} ${assignedInstaller.lastName}`
                        : task.assignedInstallerName
                    : undefined,
            });
            setTask(updated);
            setDetailsDirty(false);
            setDetailsSaved(true);
            setTimeout(() => setDetailsSaved(false), 2500);
        }
        catch (e) {
            setDetailsError(e.message ?? 'Failed to save changes');
        }
        finally {
            setDetailsSaving(false);
        }
    };
    const handleAddNote = async () => {
        if (!task || !noteText.trim())
            return;
        setNoteSubmitting(true);
        setNoteError(null);
        try {
            const authorId = user?.userId ?? user?.username ?? 'unknown';
            const authorName = user?.email ?? user?.username ?? 'Unknown';
            const updated = await addNote(task.id, {
                text: noteText.trim(),
                authorName,
                authorId,
            });
            setTask(updated);
            setNoteText('');
        }
        catch (e) {
            setNoteError(e.message ?? 'Failed to add note');
        }
        finally {
            setNoteSubmitting(false);
        }
    };
    const formatDateTime = (ts) => {
        try {
            return new Date(ts).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            });
        }
        catch {
            return ts;
        }
    };
    const formatDate = (d) => {
        if (!d)
            return '';
        const [year, month, day] = d.split('-');
        return `${month}/${day}/${year}`;
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error || !task) {
        return (_jsxs(Layout, { children: [_jsx("div", { style: styles.errorBanner, children: error ?? 'Task not found' }), _jsx("button", { style: styles.backButton, onClick: () => navigate('/tasks'), children: "\u2190 Back to Tasks" })] }));
    }
    const sortedNotes = [...task.notes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return (_jsx(Layout, { children: _jsxs("div", { style: { maxWidth: isMobile ? '100%' : '720px', margin: '0 auto' }, children: [_jsxs("div", { style: styles.pageHeader, children: [_jsx("button", { style: styles.backButton, onClick: () => navigate('/tasks'), children: "\u2190 Back" }), _jsxs("div", { style: styles.headerRow, children: [_jsx("h2", { style: styles.heading, children: task.name }), _jsxs("div", { style: styles.headerMeta, children: [_jsxs("div", { style: { position: 'relative', display: 'inline-block' }, children: [_jsx("select", { style: {
                                                        ...styles.statusSelect,
                                                        ...statusBadgeStyle(task.status),
                                                        padding: '4px 28px 4px 12px',
                                                        cursor: 'pointer',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none',
                                                    }, value: task.status, onChange: (e) => handleStatusChange(e.target.value), disabled: statusChanging, children: STATUS_OPTIONS.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) }), _jsx("span", { style: {
                                                        position: 'absolute',
                                                        right: '8px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        pointerEvents: 'none',
                                                        fontSize: '10px',
                                                        color: 'currentColor',
                                                    }, children: "\u25BC" })] }), task.dueDate && (_jsxs("span", { style: styles.dueDateChip, children: ["Due ", formatDate(task.dueDate)] }))] })] }), statusError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '8px' }, children: statusError })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Details" }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Task Name" }), _jsx("input", { type: "text", style: styles.input, value: editName, onChange: (e) => { setEditName(e.target.value); markDirty(); }, disabled: detailsSaving })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Instructions" }), _jsx("textarea", { style: styles.textarea, value: editInstructions, onChange: (e) => { setEditInstructions(e.target.value); markDirty(); }, disabled: detailsSaving, rows: 4, placeholder: "No instructions" })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Due Date" }), _jsx("input", { type: "date", style: styles.input, value: editDueDate, onChange: (e) => { setEditDueDate(e.target.value); markDirty(); }, disabled: detailsSaving })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Assigned To" }), _jsxs("select", { style: styles.select, value: editAssignedInstallerId, onChange: (e) => { setEditAssignedInstallerId(e.target.value); markDirty(); }, disabled: detailsSaving, children: [_jsx("option", { value: "", children: "Unassigned" }), installers.map((inst) => (_jsxs("option", { value: inst.id, children: [inst.firstName, " ", inst.lastName] }, inst.id)))] })] }), detailsError && _jsx("div", { style: styles.errorBanner, children: detailsError }), (detailsDirty || detailsSaved) && (_jsxs("div", { style: styles.saveRow, children: [detailsSaved && (_jsx("span", { style: styles.savedText, children: "Saved" })), detailsDirty && (_jsx("button", { style: { ...styles.saveButton, opacity: detailsSaving ? 0.6 : 1 }, onClick: handleSaveDetails, disabled: detailsSaving, children: detailsSaving ? 'Saving…' : 'Save Changes' }))] }))] }), _jsxs("div", { style: { ...styles.card, marginTop: '16px' }, children: [_jsx("h3", { style: styles.sectionTitle, children: "Notes" }), sortedNotes.length === 0 ? (_jsx("p", { style: styles.emptyNotes, children: "No notes yet." })) : (_jsx("div", { style: styles.notesList, children: sortedNotes.map((note) => (_jsxs("div", { style: styles.noteItem, children: [_jsxs("div", { style: styles.noteMeta, children: [_jsx("span", { style: styles.noteAuthor, children: note.authorName }), _jsx("span", { style: styles.noteTime, children: formatDateTime(note.createdAt) })] }), _jsx("p", { style: styles.noteText, children: note.text })] }, note.noteId))) })), _jsxs("div", { style: styles.addNoteArea, children: [_jsx("textarea", { style: styles.noteInput, value: noteText, onChange: (e) => setNoteText(e.target.value), placeholder: "Add a note\u2026", rows: 3, disabled: noteSubmitting }), noteError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '8px' }, children: noteError }), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }, children: _jsx("button", { style: {
                                            ...styles.addNoteButton,
                                            opacity: noteSubmitting || !noteText.trim() ? 0.5 : 1,
                                        }, onClick: handleAddNote, disabled: noteSubmitting || !noteText.trim(), children: noteSubmitting ? 'Adding…' : 'Add Note' }) })] })] })] }) }));
};
const styles = {
    pageHeader: {
        marginBottom: '24px',
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 8px 0',
        display: 'block',
        minHeight: '44px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
    },
    heading: {
        fontSize: '34px',
        fontWeight: '700',
        margin: '0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
        flex: 1,
    },
    headerMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
    },
    statusSelect: {
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        outline: 'none',
        backgroundColor: 'transparent',
        minHeight: '32px',
    },
    dueDateChip: {
        fontSize: '13px',
        color: '#86868b',
        fontWeight: '500',
        padding: '4px 10px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: '980px',
    },
    card: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        padding: '24px 28px',
    },
    sectionTitle: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.2px',
    },
    field: {
        marginBottom: '16px',
    },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    textarea: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '100px',
    },
    select: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '12px',
    },
    saveRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
    },
    savedText: {
        fontSize: '14px',
        color: '#248a3d',
        fontWeight: '600',
    },
    saveButton: {
        padding: '10px 22px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    emptyNotes: {
        fontSize: '14px',
        color: '#86868b',
        textAlign: 'center',
        padding: '20px 0',
        margin: 0,
    },
    notesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '20px',
        maxHeight: '360px',
        overflowY: 'auto',
    },
    noteItem: {
        backgroundColor: 'rgba(0,0,0,0.03)',
        borderRadius: '12px',
        padding: '12px 16px',
        border: '1px solid rgba(0,0,0,0.06)',
    },
    noteMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '6px',
        flexWrap: 'wrap',
        gap: '4px',
    },
    noteAuthor: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    noteTime: {
        fontSize: '12px',
        color: '#86868b',
    },
    noteText: {
        fontSize: '14px',
        color: '#3c3c43',
        margin: 0,
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
    },
    addNoteArea: {
        borderTop: '1px solid rgba(0,0,0,0.08)',
        paddingTop: '16px',
        marginTop: '4px',
    },
    noteInput: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '80px',
    },
    addNoteButton: {
        padding: '10px 22px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};
//# sourceMappingURL=TaskDetail.js.map